import {
  AttachMoney,
  Calculate,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { AccountBalance } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { getAllDecisionEngines } from 'lib/api';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { UpdateForm } from 'lib/api/sgr/types';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import InfoCard from 'lib/designSystem/InfoCard';
import PageHeader from 'lib/designSystem/PageHeader';
import { errorHandler } from 'lib/functions/errors';
import riskPremiumCalculator, {
  Output,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney } from 'lib/functions/utils';
import { DecisionEngine, Warranty } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

dayjs.locale('es');

type CalculationResult = Output;

interface Props {
  open: boolean;
  rateControls: RiskPremiumRates;
  form: UpdateForm;
  setForm: (form: UpdateForm) => void;
  originalWarranty: Warranty;
  isPending: boolean;
  onClose: () => void;
  handleConfirm: () => Promise<void>;
}

const ConfirmationDialog: React.FC<Props> = ({
  open,
  rateControls,
  form,
  setForm,
  originalWarranty,
  isPending,
  onClose,
  handleConfirm,
}) => {
  const dispatch = useDispatch();
  const { calculate } = riskPremiumCalculator();
  const [baseCalc, setBaseCalc] = useState<CalculationResult>();
  const [modifiedCalc, setModifiedCalc] = useState<CalculationResult>();
  const [decisionEngines, setDecisionEngines] = useState<DecisionEngine[]>([]);
  const { data: creditLineData } = getCreditLineByProduct(
    dispatch,
    originalWarranty.businessIdentifier ?? undefined,
    'sgr',
  );
  const { quotedAmount, quotedCurrency, startDate, endDate, type } = form;

  const daysDifference = dayjs(endDate).diff(dayjs(startDate), 'day');
  const amount = Number(quotedAmount);

  const originalWarrantyAmount = originalWarranty.quotedAmount;
  const isApproved = originalWarranty.status === 'APPROVED';
  const isActive = originalWarranty.status === 'ACTIVE';

  const updateField = (updates: Partial<UpdateForm>) => {
    setForm({ ...form, ...updates });
  };

  useEffect(() => {
    const performCalculations = () => {
      try {
        const original = calculate({
          quotedAmount: originalWarrantyAmount,
          startDate: dayjs(originalWarranty.startDate),
          endDate: dayjs(originalWarranty.endDate),
          quotedCurrency: originalWarranty.quotedCurrency,
          rates: originalRates,
        });

        const modified = calculate({
          quotedAmount: amount,
          startDate,
          endDate,
          quotedCurrency,
          rates: rateControls,
        });

        setBaseCalc(original);
        setModifiedCalc(modified);
      } catch (error) {
        setBaseCalc(undefined);
        setModifiedCalc(undefined);
      }
    };

    performCalculations();
  }, [
    amount,
    daysDifference,
    quotedCurrency,
    type,
    rateControls,
    creditLineData?.subProducts,
    originalWarranty,
  ]);

  const originalPremium = baseCalc?.amount || 0;
  const modifiedPremium = modifiedCalc?.amount || 0;
  const premiumDifference = modifiedPremium - originalPremium;

  const originalRates = {
    variableRate: originalWarranty.operationCostVariable ?? null,
    fixedRate: originalWarranty.operationCostFixed ?? null,
    variableDiscount: originalWarranty.discountRateVariable ?? null,
    fixedDiscount: originalWarranty.discountRateFixed ?? null,
  };

  const hasRateChanges =
    rateControls.variableRate !== originalRates.variableRate ||
    rateControls.fixedRate !== originalRates.fixedRate ||
    rateControls.variableDiscount !== originalRates.variableDiscount ||
    rateControls.fixedDiscount !== originalRates.fixedDiscount;

  const originalWarrantyPremium = baseCalc?.amount || 0;
  const totalPremiumDifference = modifiedPremium - originalWarrantyPremium;
  const hasSignificantChanges = Math.abs(totalPremiumDifference) > 1000;
  const variableRateChange = `${originalRates.variableRate?.toFixed(2)}% → ${rateControls.variableRate?.toFixed(2)}%`;
  const fixedRateChange = `${originalRates.fixedRate?.toFixed(2)} UF → ${rateControls.fixedRate?.toFixed(2)} UF`;
  const variableDiscountChange = `${originalRates.variableDiscount?.toFixed(2)}% → ${rateControls.variableDiscount?.toFixed(2)}%`;
  const fixedDiscountChange = `${originalRates.fixedDiscount?.toFixed(2)}% → ${rateControls.fixedDiscount?.toFixed(2)}%`;

  const defaultDecisionEngineId = useMemo(() => {
    const defaultEngine = decisionEngines.find(
      (engine: DecisionEngine) => engine.selected,
    );
    return defaultEngine?.id;
  }, [decisionEngines]);

  useEffect(() => {
    const fetchAllDecisionEngines = async () => {
      try {
        const response = await getAllDecisionEngines(dispatch);
        const engines = response.filter(
          (engine: DecisionEngine) =>
            engine.revisionObjectiveId === 'sgr-warranty-request',
        );
        setDecisionEngines(engines);
      } catch (error) {
        errorHandler(
          dispatch,
          error,
          'Error al obtener los motores de decisión',
        );
      }
    };
    void fetchAllDecisionEngines();
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <PageHeader.TitleOnly
          title="Confirmación de Edición de Garantía"
          iconType="calculate"
          subtitle={hasRateChanges ? 'Tasas Modificadas' : 'Tasas Estándar'}
        />
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Grid container>
            <Grid item xs={3}>
              <InfoCard
                icon={<AttachMoney />}
                label="Monto de Garantía"
                value={formatMoney(amount) + ' ' + quotedCurrency}
                color="blue"
              />
            </Grid>
            <Grid item xs={3}>
              <InfoCard
                icon={<Calculate />}
                label="Costo de emisión"
                value={formatMoney(modifiedPremium) + ' CLP'}
                color="green"
              />
            </Grid>
            {hasSignificantChanges && (
              <Grid item xs={3}>
                <InfoCard
                  icon={
                    totalPremiumDifference > 0 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )
                  }
                  label="Variación"
                  value={formatMoney(totalPremiumDifference) + ' CLP'}
                  color={totalPremiumDifference > 0 ? 'green' : 'red'}
                />
              </Grid>
            )}
            <Grid item xs={3}>
              <InfoCard
                icon={<AccountBalance />}
                label="Fogape"
                value={form.fogape ? `Si / ${form.fogape}%` : 'No / -'}
                color="orange"
              />
            </Grid>
            <Grid item xs={3}></Grid>
          </Grid>

          {hasRateChanges && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.8rem', mb: 1 }}
                >
                  Cambios en Tasas:
                </Typography>
              </Grid>
              {rateControls.variableRate !== originalRates.variableRate && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Tasa Variable"
                    value={variableRateChange}
                    color={
                      (rateControls.variableRate ?? 0) >
                      (originalRates.variableRate ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.fixedRate !== originalRates.fixedRate && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Tasa Fija"
                    value={fixedRateChange}
                    color={
                      (rateControls.fixedRate ?? 0) >
                      (originalRates.fixedRate ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.variableDiscount !==
                originalRates.variableDiscount && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Descuento Variable"
                    value={variableDiscountChange}
                    color={
                      (rateControls.variableDiscount ?? 0) >
                      (originalRates.variableDiscount ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.fixedDiscount !== originalRates.fixedDiscount && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Descuento Fijo"
                    value={fixedDiscountChange}
                    color={
                      (rateControls.fixedDiscount ?? 0) >
                      (originalRates.fixedDiscount ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <FormControl
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.runDecisionEngine}
                  disabled={decisionEngines.length === 0}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateField({
                      runDecisionEngine: checked,
                      decisionEngineId:
                        checked && defaultDecisionEngineId != null
                          ? defaultDecisionEngineId
                          : null,
                    });
                  }}
                  color="primary"
                />
              }
              label="Deseo ejecutar el motor de decisión para el cliente"
            />
            {form.runDecisionEngine && (
              <FormControl>
                <InputLabel id="sgr-decision-engine-label">
                  Motor de decisión
                </InputLabel>
                <Select
                  labelId="sgr-decision-engine-label"
                  label="Motor de decisión"
                  value={form.decisionEngineId ?? ''}
                  onChange={(e) =>
                    updateField({
                      decisionEngineId: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                >
                  <MenuItem value="">
                    <em>Seleccionar motor</em>
                  </MenuItem>
                  {decisionEngines.map((engine: DecisionEngine) => (
                    <MenuItem key={engine.id} value={engine.id}>
                      {engine.name}
                      {engine.selected ? ' (Predeterminado)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </FormControl>
        </Box>

        {isActive && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.emitCertificate}
                  disabled={decisionEngines.length === 0}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateField({ emitCertificate: checked });
                  }}
                  color="primary"
                />
              }
              label="Reemitir certificado de la garantía con los datos actualizados"
            />
          </Box>
        )}

        {hasRateChanges && (
          <AlertBanner
            title="Tasas Modificadas"
            message={`Las tasas han sido modificadas respecto a la cotización original. El costo de emisión ha variado en ${formatMoney(Math.abs(premiumDifference))} CLP (${premiumDifference > 0 ? 'incremento' : 'reducción'}).`}
            severity="warning"
          />
        )}

        {isApproved && (
          <AlertBanner
            title="Garantía Aprobada"
            message="Al editar una garantía aprobada, el documento de pago generado por el costo de emisión será eliminado automáticamente. Se creará un nuevo documento de pago con los valores actualizados."
            severity="info"
          />
        )}

        {hasSignificantChanges && (
          <AlertBanner
            title="Cambios Significativos Detectados"
            message={`Se han detectado cambios significativos en el costo de emisión (${formatMoney(Math.abs(totalPremiumDifference))} CLP). Se recomienda ejecutar el motor de decisiones para re-evaluar la garantía.`}
            severity="warning"
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleConfirm}
          loading={isPending}
          disabled={isPending}
        >
          Confirmar y Actualizar Garantía
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
